import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EngineerAssignModal } from "../components/EngineerAssignModal";
import { GeoLocationFilters } from "../components/GeoLocationFilters";
import { PageHeader } from "../components/PageHeader";
import { SubcontractorAssignModal } from "../components/SubcontractorAssignModal";
import { useAppState } from "../context/AppStateContext";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import type { ApplicationStatus } from "../types";
import { applicationMatchesGeoFilter } from "../utils/applicationGeoFilter";
import { canAssignEngineer, isEngineerLinkageComplete } from "../utils/engineer";
import { canAccessSubcontractorStep, isSubcontractorLinkageComplete } from "../utils/subcontractor";
import { toDisplayCamelCase } from "../utils/textCase";

/** Only these pipeline stages appear on Beneficiaries (subcontractor assignment). */
const BENEFICIARY_LIST_STATUSES: readonly ApplicationStatus[] = ["icici_recorded", "subcontractor_linked"];

const STATUS_FILTER_OPTIONS: { value: ApplicationStatus | "all"; label: string }[] = [
  { value: "all", label: "Awaiting + linked" },
  { value: "icici_recorded", label: "Awaiting subcontractor" },
  { value: "subcontractor_linked", label: "Subcontractor linked" },
];

function statusBadge(status: string) {
  if (status === "on_hold") return <span className="badge badge--hold">On hold</span>;
  if (status === "subcontractor_linked") return <span className="badge badge--complete">Subcontractor linked</span>;
  if (status === "icici_recorded") return <span className="badge badge--banking">Awaiting subcontractor</span>;
  if (status === "ready_for_mou") return <span className="badge badge--progress">Ready for MOU</span>;
  if (status === "ready_for_icici") return <span className="badge badge--progress">Ready for ICICI</span>;
  return <span className="badge badge--pending">Pending validation</span>;
}

export function SubcontractorToBeneficiary() {
  const { store: geoStore } = useGeoDirectory();
  const { applications, getSubcontractorMaster, getEngineerMaster } = useAppState();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "all">("all");
  const [districtFilter, setDistrictFilter] = useState("");
  const [mandalFilter, setMandalFilter] = useState("");
  const [villageFilter, setVillageFilter] = useState("");
  const [assignSubcontractorForId, setAssignSubcontractorForId] = useState<string | null>(null);
  const [assignEngineerForId, setAssignEngineerForId] = useState<string | null>(null);

  const eligible = useMemo(
    () => applications.filter((a) => BENEFICIARY_LIST_STATUSES.includes(a.status)),
    [applications],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return eligible.filter((a) => {
      if (q) {
        const haystack = [a.mobile.applicationId, a.mobile.beneficiaryName, a.mobile.houseSanctionNumber]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (!applicationMatchesGeoFilter(a, { district: districtFilter, mandal: mandalFilter, village: villageFilter }, geoStore)) {
        return false;
      }
      return true;
    });
  }, [eligible, search, statusFilter, districtFilter, mandalFilter, villageFilter, geoStore]);

  const hasFilters =
    search.trim() !== "" ||
    statusFilter !== "all" ||
    districtFilter !== "" ||
    mandalFilter !== "" ||
    villageFilter !== "";

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Beneficiaries" }]}
        title="Beneficiaries"
      />

      <p className="section-lead" style={{ marginBottom: "1rem" }}>
        Pick a beneficiary below to link a registered firm (work order upload is optional) and, when unlocked, assign a
        site engineer from <Link to="/engineer-onboarding">Engineer onboarding</Link>. Register firms under{" "}
        <Link to="/subcontractor-onboarding">Subcontractor</Link> if needed. For earlier pipeline stages use{" "}
        <Link to="/applications">Applicants</Link>.
      </p>

      <div className="card card--flush card--elevated">
        <div className="table-toolbar table-toolbar--stack">
          <div className="table-toolbar__top">
            <span className="table-toolbar__count">
              {hasFilters
                ? `${filtered.length} of ${eligible.length} beneficiaries`
                : `${eligible.length} beneficiaries`}
            </span>
            {hasFilters ? (
              <button
                type="button"
                className="btn btn--link btn--sm table-toolbar__clear"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                  setDistrictFilter("");
                  setMandalFilter("");
                  setVillageFilter("");
                }}
              >
                Clear filters
              </button>
            ) : null}
          </div>
          <div className="table-filters-grid">
            <div className="table-filters table-filters--beneficiary-row" role="search" aria-label="Filter beneficiaries">
              <div className="field table-filters__field table-filters__field--grow">
                <label htmlFor="sub-ben-search">Search</label>
                <input
                  id="sub-ben-search"
                  type="search"
                  placeholder="Application ID, beneficiary, or sanction ref."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="field table-filters__field">
                <label htmlFor="sub-ben-status">Pipeline status</label>
                <select
                  id="sub-ben-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as ApplicationStatus | "all")}
                >
                  {STATUS_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-filters table-filters--geo-row" aria-label="Location filters">
              <GeoLocationFilters
                idPrefix="sub-ben-geo"
                district={districtFilter}
                mandal={mandalFilter}
                village={villageFilter}
                onDistrictChange={(d) => {
                  setDistrictFilter(d);
                  setMandalFilter("");
                  setVillageFilter("");
                }}
                onMandalChange={(m) => {
                  setMandalFilter(m);
                  setVillageFilter("");
                }}
                onVillageChange={setVillageFilter}
              />
            </div>
          </div>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Beneficiary</th>
                <th>Sanction ref.</th>
                <th>District</th>
                <th>Mandal</th>
                <th>Village</th>
                <th>Pipeline status</th>
                <th>Subcontractor</th>
                <th>Engineer</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {eligible.length === 0 ? (
                <tr>
                  <td colSpan={10} className="data-table__empty">
                    No beneficiaries are in <strong>Awaiting subcontractor</strong> or{" "}
                    <strong>Subcontractor linked</strong> yet. Earlier pipeline stages stay in{" "}
                    <Link to="/applications">Applicants</Link>.
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="data-table__empty">
                    No beneficiaries match the current filters.
                  </td>
                </tr>
              ) : (
                filtered.map((a) => {
                  const mid = a.subcontractor.subcontractorMasterId;
                  const master = mid ? getSubcontractorMaster(mid) : undefined;
                  const assigned =
                    isSubcontractorLinkageComplete(a.subcontractor, master) && Boolean(a.subcontractor.linkedAt);
                  const canAssign = canAccessSubcontractorStep(a);
                  const eid = a.engineer?.engineerMasterId;
                  const engMaster = eid ? getEngineerMaster(eid) : undefined;
                  const eng = a.engineer ?? { engineerMasterId: null };
                  const engineerAssigned = isEngineerLinkageComplete(eng, engMaster) && Boolean(eng.linkedAt);
                  const canAssignEng = canAssignEngineer(a);
                  return (
                    <tr key={a.id}>
                      <td className="mono">{a.mobile.applicationId}</td>
                      <td>
                        <span className="table-name">{toDisplayCamelCase(a.mobile.beneficiaryName)}</span>
                      </td>
                      <td className="mono text-muted">{a.mobile.houseSanctionNumber}</td>
                      <td>{toDisplayCamelCase(geoStore?.findDistrict(a.district)?.registrationDistrictName ?? a.district)}</td>
                      <td>{toDisplayCamelCase(a.mandal)}</td>
                      <td>{toDisplayCamelCase(a.village)}</td>
                      <td>{statusBadge(a.status)}</td>
                      <td>
                        {assigned ? (
                          <span className="table-name">{toDisplayCamelCase(master?.firmName) || "—"}</span>
                        ) : canAssign ? (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm btn--tg"
                            onClick={() => {
                              setAssignEngineerForId(null);
                              setAssignSubcontractorForId(a.id);
                            }}
                          >
                            Assign
                          </button>
                        ) : (
                          <span className="text-muted" title="Complete ICICI, MOU, and bill checks before assigning.">
                            Locked
                          </span>
                        )}
                      </td>
                      <td>
                        {engineerAssigned ? (
                          <span className="table-name">{toDisplayCamelCase(engMaster?.fullName) || "—"}</span>
                        ) : canAssignEng ? (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm btn--tg"
                            onClick={() => {
                              setAssignSubcontractorForId(null);
                              setAssignEngineerForId(a.id);
                            }}
                          >
                            Assign
                          </button>
                        ) : (
                          <span
                            className="text-muted"
                            title="Complete ICICI, MOU, and bill checks before assigning (same gate as subcontractor)."
                          >
                            Locked
                          </span>
                        )}
                      </td>
                      <td>
                        <Link
                          to={`/applications/${a.id}/subcontractor`}
                          state={{ from: "beneficiaries" }}
                          className="btn btn--primary btn--sm btn--tg"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SubcontractorAssignModal
        open={assignSubcontractorForId !== null}
        applicationId={assignSubcontractorForId}
        onClose={() => setAssignSubcontractorForId(null)}
      />
      <EngineerAssignModal
        open={assignEngineerForId !== null}
        applicationId={assignEngineerForId}
        onClose={() => setAssignEngineerForId(null)}
      />
    </>
  );
}
